import { ChannelState } from '../enums/channel-state.enum';
import { CycleController } from '../store/models/controllers';
import { IoTDataResponse } from '../store/models/cycles';
import { ChannelActionItem } from '../store/models/channels';
import { IControllerSettings } from '../types/controller-types';
import { FaultedChannelData } from '../components/ListFaultedChannels/types/FaultedChannelData';
import getChannelActionItems from '../components/Cycle/utils/getChannelActionItems';
import getChannelNumByString from './getChannelNumByString';

export type FaultedCycleGroup = {
  cycleId: string;
  cycleName: string;
  faults: FaultedChannelData[];
  resetPayload: ChannelActionItem[];
};

export type FaultCheckableCycle = {
  id: string;
  name: string;
  cycle_controllers?: CycleController[];
  controllersSettings?: Record<string, IControllerSettings>;
  deviceNames?: Record<string, string>;
  iotDataResponse?: { [key: string]: IoTDataResponse[] } | IoTDataResponse[];
};

const getConnectedControllers = (controllers: CycleController[]): Set<string> => {
  return controllers.reduce((accumulator, cycleController) => {
    if (cycleController.controller.connected) {
      accumulator.add(cycleController.controller.serial_number);
    }

    return accumulator;
  }, new Set<string>());
};

const getFaultedControllers = (data: Record<string, IControllerSettings>) => {
  const result: Record<string, { value: string[] }> = {};

  for (const [deviceId, deviceData] of Object.entries(data ?? {})) {
    const faults = deviceData.faults?.value;
    if (Array.isArray(faults) && faults.length > 0) {
      result[deviceId] = deviceData.faults;
    }
  }

  return result;
};

const getControllerNames = (controllers: CycleController[]): Record<string, string> => {
  return controllers.reduce((accumulator, cycleController) => {
    accumulator[cycleController.controller.serial_number] = cycleController.controller.name;
    return accumulator;
  }, {} as Record<string, string>);
};

// Mirrors the per-channel fault classification already used for the single-cycle
// "Faulted Channels" popup (CycleCell.tsx), generalized to run once per cycle so a
// page can aggregate faults across every cycle it shows.
export const getFaultedCycleGroup = (
  cycleId: string,
  cycleName: string,
  cycleControllers: CycleController[],
  controllersSettings: Record<string, IControllerSettings>,
  deviceNames: Record<string, string> | undefined,
  iotDataResponse: IoTDataResponse[],
): FaultedCycleGroup | null => {
  if (!cycleControllers?.length || !iotDataResponse?.length) {
    return null;
  }

  const connectedControllers = getConnectedControllers(cycleControllers);
  const faultedControllers = getFaultedControllers(controllersSettings);
  const controllerNames = getControllerNames(cycleControllers);
  const channelNames = deviceNames ?? {};

  const faults = iotDataResponse.reduce<FaultedChannelData[]>((accumulator, item) => {
    if (!item?.channel) {
      return accumulator;
    }

    if (!connectedControllers.has(item.serial)) {
      accumulator.push({
        channelName: channelNames[item.pk] || getChannelNumByString(item.channel),
        controllerName: controllerNames[item.serial] || item.serial,
        id: item.pk,
        isControllerConnected: false,
        timestamp: item.sk,
      });
      return accumulator;
    }

    if (
      faultedControllers[item.serial] ||
      item.state === ChannelState.FAULTED ||
      (item.manualFault !== null && item.manualFault !== undefined)
    ) {
      const channelFaultMessage = item.manualFault?.message ? [item.manualFault.message] : undefined;
      accumulator.push({
        channelName: channelNames[item.pk] || getChannelNumByString(item.channel),
        controllerName: controllerNames[item.serial] || item.serial,
        id: item.pk,
        isControllerConnected: true,
        timestamp: item.manualFault?.faultDatetime || item.sk,
        controllerFaultMessages: faultedControllers[item.serial]?.value,
        channelFaultMessage,
      });
    }

    return accumulator;
  }, []);

  if (!faults.length) {
    return null;
  }

  // Only actually-faulted, reachable channels are eligible to be reset - a
  // disconnected controller can't accept a reset command, and non-faulted
  // channels shouldn't be included just because they share a cycle.
  const resetPayload = getChannelActionItems(cycleControllers, iotDataResponse, controllersSettings);

  return { cycleId, cycleName, faults, resetPayload };
};

export const getFaultedCycleGroups = (cycles: FaultCheckableCycle[]): FaultedCycleGroup[] => {
  return cycles.reduce<FaultedCycleGroup[]>((accumulator, cycle) => {
    const group = getFaultedCycleGroup(
      cycle.id,
      cycle.name,
      cycle.cycle_controllers ?? [],
      cycle.controllersSettings ?? {},
      cycle.deviceNames,
      Array.isArray(cycle.iotDataResponse) ? cycle.iotDataResponse : [],
    );

    if (group) {
      accumulator.push(group);
    }

    return accumulator;
  }, []);
};
